<template>
  <div class="news-article container">
    <div class="news-article__header">
      <div class="news-article__header-section">
        <Tag class="news-article__scope" data-aos="fade-right" v-if="type">
          {{ type }}
        </Tag>
        <p
          class="news-article__property"
          v-for="(property, i) in properties"
          :key="property.icon"
          data-aos="fade-right"
          :data-aos-delay="(i + 1) * 200"
        >
          <i class="fa-solid" :class="property.icon" />
          {{ property.value }}
        </p>
      </div>
      <div class="news-article__header-section">
        <Button
          :href="editMode ? '' : backTo === 'home' ? '/' : '/news'"
          data-aos="fade-left"
          data-aos-delay="600"
        >
          <i class="fa-solid fa-angle-left" />
          Back to {{ backTo ?? "all news" }}
        </Button>
      </div>
    </div>
    <h1 class="news-article__title" v-if="title">
      {{ title }}
    </h1>
    <img
      class="news-article__preview-image"
      alt="preview"
      :src="previewImageUri"
      v-if="previewImageUri && !hidePreviewImage"
    />
    <Article
      class="news-article__content"
      v-model:content="content"
      :edit-mode="editMode"
    />
  </div>
  <div class="news-article__footer" v-if="!editMode">
    <ScreenHeader class="news-article__footer-header" data-aos="fade-up">
      <template #subtitle> Other News </template>
    </ScreenHeader>
    <div class="container">
      <div class="news-article__articles" v-if="highlightedArticles">
        <BlogArticleCard
          class="news-article__article-card"
          v-for="(article, i) in highlightedArticles"
          :key="article.id"
          :background-image="article.previewImageUri"
          :published-at="article.publishedAt"
          :title="article.title"
          :views="article.views"
          :url="article.url"
          :short-description="article.shortDescription"
          :type="article.type"
          data-aos="fade"
          data-aos-delay="100"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  export interface NewsArticleProps {
    id?: number;
    title?: string | null;
    publishedAt?: Date | null;
    previewImageUri?: string | null;
    hidePreviewImage?: boolean | null;
    type?: string | null;
    editMode?: boolean;
  }

  const props = defineProps<NewsArticleProps>();
  const route = useRoute();

  const backTo = computed(() => route.query.backTo);

  const content = defineModel<string | null>("content");
  const { getAllNews } = useWebsiteContent();

  const properties = computed<
    {
      icon: string;
      value: string | number;
    }[]
  >(() => {
    const properties = [];

    if (props.publishedAt) {
      properties.push({
        icon: "fa-calendar",
        value: props.publishedAt.toLocaleDateString("en-US", {
          day: "numeric",
          month: "long",
          year: "numeric",
        }),
      });
    }

    return properties;
  });

  const { data: articles, suspense: suspenseArticles } = getAllNews();
  const highlightedArticles = computed(
    () =>
    articles.value?.data
    .sort(
      (articleA, articleB) =>
      Number(articleB.highlighted) - Number(articleA.highlighted)
    )
    .filter((article) => article.id !== props.id)
    .slice(0, 3) ?? []
  );
  
  await suspenseArticles();
</script>

<style scoped lang="scss">
  .news-article {
    display: flex;
    flex-direction: column;
    gap: 16px;

    padding-top: 64px;
    padding-bottom: 64px;

    &__header {
      display: flex;
      align-items: center;
      gap: 24px;
      justify-content: space-between;

      margin-bottom: 32px;
    }

    &__header-section {
      display: flex;
      align-items: center;
      gap: 24px;

      @media (max-width: $sm) {
        align-items: center;
        flex-wrap: wrap;
        gap: 16px;
      }
    }

    &__go-back {
      color: #212529;
      font-family: var(--font-family-main);
      font-size: 18px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;

      @media (max-width: $sm) {
        display: none;
      }
    }

    &__scope {
    }

    &__property {
      display: flex;
      align-items: center;
      gap: 8px;

      color: #212529;
      font-family: var(--font-family-main);
      font-size: 18px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }

    &__title {
      align-self: center;
      width: 75%;

      font-family: var(--font-family-main);
      font-weight: 600;
      font-size: 56px;
      line-height: 1.1;
      text-align: center;

      @media (max-width: $lg) {
        width: 100%;
      }
    }

    &__properties {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 16px;
    }

    &__property {
      display: flex;
      align-items: center;
      gap: 10px;

      font-family: var(--font-family-main);
      font-size: 18px;
      color: #212529;
    }

    &__property-icon {
      opacity: 0.5;
    }

    &__preview-image {
      margin: 16px 0;

      //aspect-ratio: 2.5 / 1;
      width: 100%;
      object-fit: cover;

      border-radius: 16px;
    }

    &__content {
      width: 80%;
      margin: 0 auto;

      @media (max-width: $lg) {
        width: 100%;
      }
    }

    &__footer {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;

      padding: 64px 0;

      background-image: url("@/assets/images/job-application-bg.svg");
      background-size: cover;
      background-position: top center;
    }

    &__articles {
      display: flex;
      justify-content: center;
      gap: 32px;

      @media (max-width: $md) {
        flex-direction: column;
      }
    }

    &__article-card {
      flex: 0 0 30%;
    }
  }
</style>
